import { computed } from 'vue';
import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia';

import { useOrderStore } from '@/stores/order';
import { useActiveUserStore } from '@/stores/user';

import type { MaterialRequest } from '@cutr/constants/material-request';
import type { MaterialRequestRow } from '@/api/materialRequests';
import {
  useInvalidateMaterialRequests,
  useMaterialRequestMutations,
  useMaterialRequestsQuery,
} from '@/queries/materialRequests';

/** What `applyParts` and its siblings need of a request, saved or not. */
type PartHolder = Pick<MaterialRequest, 'id' | 'parts'>;

const MATERIAL_KEYS = [
  'core',
  'layer1',
  'layer2',
  'hardware',
  'other',
  'supplierHardware',
  'coreSupplierMaterial',
  'layer1SupplierMaterial',
  'layer2SupplierMaterial',
] as const satisfies readonly (keyof MaterialRequestRow)[];

export const useMaterialRequestStore = defineStore('materialRequest', () => {
  const orderStore = useOrderStore();
  const { activeVariantId } = storeToRefs(orderStore);
  const { isMaker } = storeToRefs(useActiveUserStore());

  // The endpoint serves admins, customers and share-link sessions alike; a
  // cutlist order has no material requests to ask for.
  const canSeeMaterialRequests = computed(
    () => !isMaker.value && !orderStore.isCutlist
  );

  const { materialRequests, query } = useMaterialRequestsQuery(
    activeVariantId,
    { enabled: canSeeMaterialRequests }
  );
  const mutations = useMaterialRequestMutations(activeVariantId);
  const invalidateMaterialRequests = useInvalidateMaterialRequests();

  const isLoading = computed(() => query.isLoading.value);

  const materialRequestsWithMaterials = computed(() =>
    materialRequests.value.filter(
      (request) =>
        request.parts?.length || MATERIAL_KEYS.some((key) => request[key])
    )
  );

  const nodesWithRequestMap = computed(() => {
    const map = {} as Record<string, MaterialRequestRow>;
    for (const request of materialRequests.value) {
      if (!request.parts) continue;
      for (const part of request.parts) {
        map[part.id] = request;
      }
    }

    return map;
  });

  const byFileId = computed(() =>
    materialRequests.value
      .filter((request) => request.cadFileId && request.pdfPageReference)
      .reduce(
        (acc, request) => {
          const fileId = request.cadFileId!;
          const pdfPage = request.pdfPageReference!;
          acc[fileId] ||= {};
          acc[fileId][pdfPage] ||= [];
          acc[fileId][pdfPage].push(request);
          return acc;
        },
        {} as Record<string, Record<string, MaterialRequestRow[]>>
      )
  );

  function updateMaterialRequestParts(
    materialRequest: PartHolder,
    partIds: string[]
  ) {
    return mutations.updateMaterialRequest({
      id: materialRequest.id,
      partIds: [...new Set(partIds)],
    });
  }

  function applyParts(request: PartHolder, selectedPartIds: string[]) {
    const updatedPartIds = [
      ...(request.parts || []).map((p) => p.id),
      ...selectedPartIds,
    ];
    return updateMaterialRequestParts(request, updatedPartIds);
  }

  function unapplyParts(
    materialRequest: PartHolder,
    selectedPartIds: string[]
  ) {
    if (!materialRequest?.parts) return Promise.resolve();

    const updatedPartIds = materialRequest.parts
      .filter((part) => !selectedPartIds.includes(part.id))
      .map((part) => part.id);

    return updateMaterialRequestParts(materialRequest, updatedPartIds);
  }

  return {
    materialRequests,
    isLoading,
    applyParts,
    unapplyParts,
    updateMaterialRequestParts,
    byFileId,
    materialRequestsWithMaterials,
    nodesWithRequestMap,
    // For writes elsewhere that regenerate the parts a request holds.
    invalidateMaterialRequests: () =>
      invalidateMaterialRequests(activeVariantId.value),
    ...mutations,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useMaterialRequestStore, import.meta.hot)
  );
}
